import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { FullScreenLoader } from "@/components/feedback/FullScreenLoader";
import type { PermissionKey } from "@/lib/permissions";

interface RequirePermissionProps {
  permission: PermissionKey;
  children: ReactNode;
}

/**
 * UI route guard backed by role_permissions. This is deliberately separate
 * from RLS: RLS remains the data security boundary, while this guard keeps
 * unauthorized users out of screens they cannot use.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { hasPermission, isLoading, isAuthorizing } = useAuth();

  if (isLoading || isAuthorizing) {
    return <FullScreenLoader message="Checking permissions…" />;
  }

  if (!hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
